/**
 * Client-side Form.io schema flattener.
 *
 * Mirrors the server's flattenFormFields (apps/api/functions/ai/intake-session.ts)
 * so the AI chat's FieldTracker and the ConfirmationScreen show the SAME leaf input
 * fields the AI was asked to collect — keyed identically, so collected values line up.
 *
 * Form.io nests inputs inside layout containers (panel, columns, fieldset, well,
 * container). A naive top-level `.map()` over schemaJson.components returns those
 * containers as if they were fields (e.g. a "Panel" row with no value), which is
 * exactly the bug this replaces. Keep this in lockstep with the server flattener.
 */

export interface FlatFormField {
  key: string;
  label: string;
  type: string;
  required: boolean;
}

interface RawComponent {
  key?: string;
  label?: string;
  type?: string;
  input?: boolean;
  validate?: { required?: boolean };
  components?: RawComponent[];
}

// Layout containers: recurse into their children, never emit them as fields.
// Kept identical to the server (panel, columns, fieldset, well, container) so the
// review shows exactly the fields the AI collected. NOTE: like the server, a
// Form.io `columns` component keeps its children under comp.columns[].components,
// which neither side currently walks — columns-nested fields are a known gap to
// fix in BOTH places together (would change what the AI collects), out of scope
// for this review-render fix.
const CONTAINER_TYPES = new Set(['panel', 'columns', 'fieldset', 'well', 'container']);

export function flattenFormFields(components: RawComponent[] | undefined | null): FlatFormField[] {
  const fields: FlatFormField[] = [];

  const walk = (comps: RawComponent[]): void => {
    for (const comp of comps) {
      if (!comp || typeof comp !== 'object') continue;
      const type = comp.type ?? '';

      if (CONTAINER_TYPES.has(type)) {
        if (Array.isArray(comp.components)) walk(comp.components);
        continue;
      }

      // Non-input display components carry no value.
      if (type === 'content' || type === 'htmlelement' || type === 'button') continue;

      if (type === 'signature') {
        if (comp.key) {
          fields.push({ key: comp.key, label: comp.label ?? 'Signature', type: 'signature', required: Boolean(comp.validate?.required) });
        }
        continue;
      }

      // Standard input leaf.
      if (comp.input !== false && comp.key) {
        fields.push({ key: comp.key, label: comp.label ?? comp.key, type, required: Boolean(comp.validate?.required) });
      }
    }
  };

  if (Array.isArray(components)) walk(components);
  return fields;
}
